import { useId } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, LogOut } from 'lucide-react';
import { getErrorMessage, keys, portalQueries } from '@blimo/api-client';
import { useApi, useMe, useRefreshMe, useTwoFactorActions } from '@blimo/api-client/react';
import { CONSENT_LABELS, CONTACT_KIND_LABELS, labelOf, ROLE_LABELS } from '@blimo/shared';
import { Alert, AlertDescription, Badge, BrandingLegalLinks, Button, DescriptionList, ErrorState, Skeleton, initials, toast, useBranding } from '@blimo/ui';
import { ChangePasswordForm, TwoFactorSettings } from '@blimo/ui/auth';
import { PageHeader } from '../components/ui/page-header.jsx';
import { Section } from '../components/ui/section.jsx';
import { Switch } from '../components/ui/switch.jsx';
import { ThemeSegmented } from '../components/ui/theme-segmented.jsx';
import { useLogout } from '../lib/use-logout.js';

const CONSENT = 'share_contact_with_co_owners';

/**
 * Mein Profil (F-008/F-016): Stammdaten nur lesend aus GET /portal/profile (Quelle Verwaltung/WinCasa/Cobra),
 * Einwilligung „Kontaktdaten für Miteigentümer sichtbar“ (PATCH /portal/profile/consents), Farbschema,
 * Passwort (Fortify PUT /auth/user/password, 422 an den Feldern) und Zwei-Faktor-Einstellungen.
 */
export function ProfilePage() {
  const api = useApi();
  const { data: me } = useMe();
  const profile = useQuery(portalQueries.profile(api));
  const twoFactorActions = useTwoFactorActions();
  const refreshMe = useRefreshMe();
  const logout = useLogout();
  if (!me) return null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Mein Profil"
        compactTitle="Profil"
        description="Ihre Angaben, Einwilligungen und Sicherheitseinstellungen."
        actions={
          <Button variant="outline" onClick={logout}>
            <LogOut aria-hidden="true" /> Abmelden
          </Button>
        }
      />
      <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3.5">
        <span className="portal-avatar size-11 text-sm" aria-hidden="true">
          {initials(me.name)}
        </span>
        <div className="flex min-w-0 flex-col">
          <p className="break-words text-base font-semibold">{me.name}</p>
          <p className="break-all text-[0.8125rem] text-muted-foreground">{me.email}</p>
        </div>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Section title="Persönliche Angaben" description="Aus Ihrer Verwaltung übernommen (nur lesend)" bodyClassName="flex flex-col gap-4">
            <Alert variant="info">
              <Info aria-hidden="true" />
              <AlertDescription>Änderungen bitte an die Verwaltung – Name, Anschrift und Kontaktdaten werden dort gepflegt.</AlertDescription>
            </Alert>
            {profile.error ? (
              <ErrorState compact error={profile.error} onRetry={() => profile.refetch()} />
            ) : profile.isPending ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <MasterData profile={profile.data} roles={me.roles} />
            )}
          </Section>

          {profile.data?.party && <ConsentSection profile={profile.data} />}

          <Section title="Darstellung" description="Farbschema des Portals auf diesem Gerät">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium">Farbschema</span>
              <ThemeSegmented />
            </div>
          </Section>
        </div>

        <div className="flex flex-col gap-4">
          <Section title="Passwort ändern" description="Andere angemeldete Sitzungen werden dabei abgemeldet.">
            <ChangePasswordForm onSubmit={(values) => api.auth.updatePassword(values)} onSuccess={() => toast.success('Passwort geändert')} />
          </Section>

          <Section
            id="zwei-faktor"
            title="Zwei-Faktor-Authentifizierung"
            description="Zusätzlicher Schutz Ihres Kontos mit einem Einmalcode."
            className="scroll-mt-20"
          >
            <TwoFactorSettings
              enabled={Boolean(me.two_factor?.enabled)}
              required={Boolean(me.two_factor?.required)}
              actions={twoFactorActions}
              onChanged={refreshMe}
            />
          </Section>

          <ManagementContact />
        </div>
      </div>
    </div>
  );
}

/** Verwaltung, Support-Kontakt, Impressum/Datenschutz aus dem Branding (nur wenn etwas hinterlegt ist). */
function ManagementContact() {
  const b = useBranding();
  if (!b.support_email && !b.support_phone && !b.imprint_url && !b.privacy_url) return null;
  return (
    <Section title="Ihre Verwaltung" description={b.company_name}>
      <BrandingLegalLinks withSupport className="justify-start text-[0.8125rem]" linkClassName="text-(--primary-text)" />
    </Section>
  );
}

/** @param {{ profile: any, roles?: string[] }} props */
function MasterData({ profile, roles }) {
  const party = profile.party;
  return (
    <DescriptionList
      labelWidth="10rem"
      items={[
        { label: 'Name', value: party?.name ?? profile.name },
        ...(party
          ? [
              { label: 'Briefanrede', value: party.letter_salutation },
              { label: 'Anschrift', value: party.address },
            ]
          : []),
        ...(party?.contacts ?? []).map((contact, index) => ({
          key: `contact-${index}`,
          label: labelOf(CONTACT_KIND_LABELS, contact.kind),
          value: <span className="break-all">{contact.value}</span>,
        })),
        { label: 'Anmelde-E-Mail', value: <span className="break-all">{profile.email}</span> },
        {
          label: 'Zwei-Faktor',
          value: (
            <span className="flex flex-wrap items-center gap-2">
              {profile.two_factor_enabled ? <Badge variant="success">Aktiv</Badge> : <Badge variant="neutral">Nicht aktiv</Badge>}
              <a href="#zwei-faktor" className="inline-flex items-center text-(--primary-text) hover:underline">
                Einstellungen
              </a>
            </span>
          ),
        },
        {
          label: 'Rollen',
          value: roles?.length ? (
            <span className="flex flex-wrap gap-1">
              {roles.map((role) => (
                <Badge key={role} variant="secondary">
                  {ROLE_LABELS[role] ?? role}
                </Badge>
              ))}
            </span>
          ) : null,
        },
      ]}
    />
  );
}

/** Consent switch; the PATCH answer (full profile) replaces the cached profile. @param {{ profile: any }} props */
function ConsentSection({ profile }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const id = useId();
  const mutation = useMutation({
    mutationFn: (granted) => api.portal.profile.updateConsents({ [CONSENT]: granted }),
    onSuccess: (updated, granted) => {
      queryClient.setQueryData(keys.portal.profile(), updated);
      toast.success(granted ? 'Einwilligung erteilt' : 'Einwilligung widerrufen');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Die Einwilligung konnte nicht gespeichert werden.')),
  });
  const checked = mutation.isPending ? Boolean(mutation.variables) : Boolean(profile.consents?.[CONSENT]);

  return (
    <Section title="Einwilligungen" description="Sie können Ihre Einwilligung jederzeit widerrufen.">
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <label htmlFor={id} className="text-sm font-medium">
            {CONSENT_LABELS[CONSENT]}
          </label>
          <p id={`${id}-hint`} className="text-[0.8125rem] text-muted-foreground">
            Miteigentümer Ihrer Liegenschaft sehen dann Ihre E-Mail-Adresse und Telefonnummer in der Eigentümerliste.
          </p>
        </div>
        <Switch
          id={id}
          aria-describedby={`${id}-hint`}
          checked={checked}
          disabled={mutation.isPending}
          onCheckedChange={(value) => mutation.mutate(value)}
          className="mt-0.5"
        />
      </div>
    </Section>
  );
}
